// Pure visibility rules for the mastering graph (see MasteryGraph.tsx).
// Extracted so the "which sites are on screen" logic can be unit-tested — it
// is the part users experience as the filters working or not working.

export interface MatchEdgeLike {
  a: string;
  b: string;
  conf: number | null;
  status: string;
}

export interface OverlayEdgeLike {
  site: string;
}

/**
 * Scored-pair edges the canvas should draw: rejected pairs only when asked
 * for, and only pairs at or above the confidence threshold. A pair with no
 * recorded confidence is treated as 0 so it disappears the moment the
 * threshold is raised off the floor.
 */
export function visibleMatchEdges<T extends MatchEdgeLike>(
  edges: readonly T[] | undefined,
  { showRejected, minConf }: { showRejected: boolean; minConf: number },
): T[] {
  return (edges ?? []).filter(
    (e) =>
      (showRejected || e.status !== "auto_rejected") &&
      (e.conf ?? 0) >= minConf,
  );
}

/**
 * Site ids that have at least one visible connection — a scored-pair edge, or
 * a link from an overlay layer (PIs, golden orgs) that is switched on.
 *
 * Overlay links count deliberately. Judging isolation on scored pairs alone
 * hid every site whose only relationship is its investigator, so turning on
 * "show PIs" appeared to do nothing on a tenant where few sites have an
 * accepted match pair.
 */
export function connectedSiteIds(
  matchEdges: readonly MatchEdgeLike[],
  ...overlays: (readonly OverlayEdgeLike[] | undefined)[]
): Set<string> {
  const ids = new Set<string>();
  matchEdges.forEach((e) => {
    ids.add(e.a);
    ids.add(e.b);
  });
  overlays.forEach((overlay) =>
    (overlay ?? []).forEach((e) => ids.add(e.site)),
  );
  return ids;
}
